/**
 * Ryan O'Dowd
 * 2024-02-27
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  NEW_GOAL_ROUTE,
  apiUpdateGoal,
  fetchWrinkles,
} from '@utils/store/actions';
import React, {
  useState,
} from 'react';
import Button from '@components/common/Button';
import CPassage from '@classes/Passage';
import Dialog from '@components/common/Dialog';
// @TODO: import ListItem from '@components/common/ListItem';
import Text from '@components/common/Text';
import {
  TouchableOpacity,
} from 'react-native';
import getStyles from './styles';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const AddToGoalDialog = (props: AddToGoalDialogProps) => {
  const navigation = hooks.useNavigation();
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const styles = hooks.useStyles(getStyles);

  const [_isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const activeGoals = hooks.useTypedSelector((state) => {
    return Object.values(state.goals).filter((g: any) => g.user_goal.status === 'active' && !g.classroom_goal_metadata?.classroom_id).sort((a: any, b: any) => {
      return a.title.localeCompare(b.title);
    });
  }, (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue));

  return (
    <React.Fragment>
      <Dialog
        visible={true}
        onDismiss={() => props.onClose()}
      >{/* @TODO: abstract this modal to it's own component and make available in Settings component as well */}
        <Dialog.Title>{i18n('addToGoal')}</Dialog.Title>
        <Dialog.Content>
          {activeGoals.map((goal: any) => { // @TODO: map over sections for goals that are at 100% and ones that are less than 100% (different from active/completed...that's more about enabled/disabled)
            return (
              <TouchableOpacity// @TODO: convert to ListItem component
                key={goal.id}
                style={styles.activeGoalOption}
                onPress={async () => {
                  const newPassage = await utilities.createNewPassage(
                    props.passage.translationId,
                    props.passage.bookId,
                    props.passage.chapterNumber,
                    props.passage.startVerseNumber,
                    props.passage.endVerseNumber,
                  );
                  const currentPassageIds = goal.modules.flatMap((module) => {
                    return module.assignments.map((assignment) => {
                      return assignment.assignment_object.passage?.id;
                    });
                  });
                  if (currentPassageIds.includes(newPassage.id)) {
                    setIsConfirmDialogOpen(true);
                  } else {
                    dispatch(apiUpdateGoal(goal.id, goal.title, null, null, null, [...currentPassageIds, newPassage.id], null, null, fetchWrinkles, null, null));
                    props.onClose();
                  }
                }}
              >
                <Text style={styles.goalOptionText}>{goal.title}</Text>
              </TouchableOpacity>
            );
          })}
          <TouchableOpacity// @TODO: convert to ListItem component
            style={[styles.activeGoalOption, styles.newGoalOption]}
            onPress={async () => {
              const passage = await utilities.createNewPassage( // @TODO: abstract this away with what's currently in passageselection and newgoal
                props.passage.translationId,
                props.passage.bookId,
                props.passage.chapterNumber,
                props.passage.startVerseNumber,
                props.passage.endVerseNumber,
              );
              const newPassage = CPassage.createFromObject(passage);
              props.onClose();
              navigation.navigate(NEW_GOAL_ROUTE, {
                initialPassages: [newPassage],
              });
            }}
          >
            <Text style={[styles.goalOptionText, styles.newGoalOptionText]}>{i18n('createNewGoal')}</Text>
          </TouchableOpacity>
        </Dialog.Content>
        <Dialog.Actions>
          <Button
            mode='contained'
            onPress={() => props.onClose()}
          >
            {i18n('cancel')}
          </Button>
        </Dialog.Actions>
      </Dialog>
      {_isConfirmDialogOpen &&
        <Dialog
          visible={_isConfirmDialogOpen}
          onDismiss={() => setIsConfirmDialogOpen(false)}
        >
          <Dialog.Title>{i18n('duplicatePassageTitle')}</Dialog.Title>
          <Dialog.Content>
            {i18n('duplicatePassageBody')}
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              mode='contained'
              onPress={() => setIsConfirmDialogOpen(false)}
            >
              {i18n('okay')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      }
    </React.Fragment>
  );
};

class AddToGoalDialogProps {
  onClose: () => void;
  passage: {
    translationId: number,
    bookId: number,
    chapterNumber: number,
    startVerseNumber: number,
    endVerseNumber: number,
  }; // @TODO: type
};

export default AddToGoalDialog;
